/**
 * @nanokart/tryon-studio-sdk
 * Core type definitions
 */

// ============================================================================
// SDK Version & Constants
// ============================================================================

export const SDK_VERSION = '2.0.0';
export const EVENT_SCHEMA_VERSION = '2.0';
export const DEFAULT_API_BASE_URL = 'https://api.nanokart.ai';

// ============================================================================
// Studio Modes
// ============================================================================

export type StudioMode = 'embedded-commerce';

// ============================================================================
// Environment
// ============================================================================

export type PlatformContext = 'standalone' | 'shopify' | 'woocommerce' | 'magento' | 'custom';

// ============================================================================
// Feature Configuration
// ============================================================================

export interface FeatureConfig {
  inventoryBrowsing?: boolean;
  multiItemSession?: boolean;
  moments?: boolean;
  cart?: boolean;
  download?: boolean;
  share?: boolean;
  /** Enable the Size Recommendation panel after a successful try-on */
  sizeRecommendation?: boolean;
}

export type ResolvedFeatures = Required<FeatureConfig>;

// ============================================================================
// Branding Configuration
// ============================================================================

export interface BrandingConfig {
  logoUrl?: string;
  storeName?: string;
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  borderRadius?: number;
  showPoweredBy?: boolean;
}

// ============================================================================
// Catalog & Products
// ============================================================================

export interface CatalogItem {
  productId: string;
  name: string;
  imageUrl: string;
  thumbnailUrl?: string;
  price: number;
  originalPrice?: number;
  currency: string;
  category?: string;
  color?: string;
  sizes?: string[];
  variantId?: string;
  /**
   * Size chart for this product, keyed by size label.
   * Values are measurement objects in cm (e.g. { chest: 90, waist: 72, hips: 96 }).
   * Can also be a raw string if the partner provides free-form chart data.
   * Used by SizeRecommendPanel when requesting a size recommendation.
   */
  sizeChartCm?: Record<string, Record<string, number>> | string | null;
  /** Gender of the garment — derived from item metadata. Used to skip asking the customer. */
  gender?: string;
  metadata?: Record<string, unknown>;
}

export interface CatalogFilters {
  category?: string;
  gender?: 'male' | 'female' | 'unisex';
  priceRange?: { min: number; max: number };
  color?: string;
  search?: string;
}

export interface CatalogPagination {
  page: number;
  limit: number;
}

export interface CatalogResult {
  items: CatalogItem[];
  total: number;
  hasMore: boolean;
}

// ============================================================================
// Try-On Results
// ============================================================================

export interface TryOnResult {
  /** tryon_key from API — used for Moments generation */
  cacheKey: string;
  productId: string;
  resultImageUrl: string;
  inputPhotoUrl: string;
  garmentImageUrl: string;
  processingTimeMs: number;
  timestamp: string;
}

export interface MomentResult {
  momentType: string;
  resultImageUrl: string;
  tryonCacheKey: string;
  processingTimeMs: number;
  timestamp: string;
}

// ============================================================================
// Cart
// ============================================================================

export interface CartItem {
  productId: string;
  product: CatalogItem;
  variantId?: string;
  size?: string;
  quantity: number;
  tryonResult?: TryOnResult;
  addedAt: string;
}

// ============================================================================
// Customer Context (for Partner Website Integration)
// ============================================================================

/**
 * Customer information passed by the partner.
 * Partners know their customers (logged-in users) and provide this context
 * so the SDK can include it in webhook payloads.
 */
export interface CustomerContext {
  /** Customer's phone number (with country code preferred) */
  phone?: string;
  /** Customer's email address */
  email?: string;
  /** Customer's name */
  name?: string;
  /** Partner's internal customer ID */
  customerId?: string;
  /** Any additional customer metadata */
  metadata?: Record<string, unknown>;
}

// ============================================================================
// Session
// ============================================================================

export interface SessionStats {
  tryonsGenerated: number;
  momentsGenerated: number;
}

// ============================================================================
// Main SDK Configuration
// ============================================================================

export interface TryOnStudioConfig {
  /**
   * Your Nanokart Partner API Key.
   * Obtain from https://nanokart.ai → Dashboard → API Keys
   */
  apiKey: string;

  /**
   * Your Nanokart Partner ID.
   * Found in your Nanokart dashboard.
   */
  partnerId: string;

  /**
   * Override the API base URL.
   * Defaults to https://api.nanokart.ai — do not change unless directed.
   */
  apiBaseUrl?: string;

  // ── Catalog ──────────────────────────────────────────────────────────────

  /**
   * Static catalog items. Pass your product list directly.
   * Each item must include productId, name, imageUrl, price, currency.
   */
  items?: CatalogItem[];

  /**
   * URL of your catalog API endpoint.
   * The SDK will GET this URL and map the response to CatalogItem format.
   * Mutually exclusive with `items` — if both provided, `items` takes precedence.
   */
  catalogUrl?: string;

  /**
   * Optional HTTP headers to include when fetching catalogUrl.
   * e.g. { Authorization: 'Bearer your-token' }
   */
  catalogHeaders?: Record<string, string>;

  /**
   * Optional function to map your catalog API response items to CatalogItem format.
   * Use this if your catalog API returns a different structure.
   *
   * @example
   * mapCatalogItem: (raw) => ({
   *   productId: raw.id,
   *   name: raw.title,
   *   imageUrl: raw.featured_image,
   *   price: raw.price / 100,
   *   currency: 'INR',
   * })
   */
  mapCatalogItem?: (raw: Record<string, unknown>) => CatalogItem;

  // ── Localisation ─────────────────────────────────────────────────────────

  /** Default currency code. Defaults to 'INR'. */
  currency?: string;

  /** Default timezone. Defaults to 'Asia/Kolkata'. */
  timezone?: string;

  // ── Mode & Features ───────────────────────────────────────────────────────

  mode?: StudioMode;
  features?: FeatureConfig;

  // ── Branding ──────────────────────────────────────────────────────────────

  brandingConfig?: BrandingConfig;

  // ── Platform ─────────────────────────────────────────────────────────────

  platformContext?: PlatformContext;
  platformMetadata?: Record<string, unknown>;

  // ── Customer Context (for Partner Website Integration) ──────────────────

  /**
   * Customer information for webhook payloads.
   * Partners integrating the SDK on their website should provide this
   * so webhooks include customer details.
   *
   * @example
   * customerContext: {
   *   phone: '+919876543210',
   *   email: 'customer@example.com',
   *   name: 'John Doe',
   *   customerId: 'cust_12345'
   * }
   */
  customerContext?: CustomerContext;
}
